/*
 * OPTRA Homepage - Production Ready Implementation
 * 
 * Developer Notes:
 * - Ribbon Implementation: Uses optimized SVG paths with gradients and backdrop effects
 *   instead of heavy 3D assets for better performance and mobile compatibility
 * - Motion Fallbacks: All animations respect prefers-reduced-motion and gracefully degrade
 * - Assets to Replace: 
 *   - Ribbon SVG paths can be swapped for GLTF 3D files in Ribbons.jsx
 *   - Logo dots can be customized via LogoDots.jsx props
 *   - Color tokens are centralized in tailwind.config.js
 * 
 * How to replace uploaded image as background reference:
 * 1. Add the image to public/images/background-reference.png
 * 2. Update the background in Homepage.jsx to use the image as a reference layer
 * 
 * How to tune colors:
 * 1. Modify color tokens in tailwind.config.js under theme.extend.colors
 * 2. Update CSS variables in src/styles/index.css
 * 
 * How to swap ribbon asset for GLTF 3D file:
 * 1. Install @react-three/fiber and @react-three/drei
 * 2. Replace SVG ribbons in Ribbons.jsx with Three.js GLTF loader
 * 3. Implement fallback SVG for mobile/low-performance devices
 */

import { useDeferredValue, useEffect, useRef, useState, Suspense } from "react";
import { forceUnlockDocumentScroll } from "./utils/bodyScrollLock";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useAuth, useUser } from "@clerk/react";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { CountryProvider } from "./contexts/CountryContext";
import ScrollProgress from "./components/ScrollProgress.jsx";
import ErrorBoundary from "./components/ErrorBoundary.jsx";
import Header from "./components/Header";
import CalEmbedSetup from "./components/CalEmbedSetup";
import RoutePending from "./components/RoutePending.jsx";
import { preloadRouteModule, routePages } from "./lib/routePreload.js";
import { getSessionEmail } from "@/lib/userSession";
import ClerkEntitlementBridge from "@/components/auth/ClerkEntitlementBridge";
import ClerkLoginUnlockBridge from "@/components/auth/ClerkLoginUnlockBridge";
import ClientGatesPrefetch from "@/components/auth/ClientGatesPrefetch";
import { isClerkBillingSyncEnabled, isClerkConfigured } from "@/lib/clerkEntitlements";
import { isLocalDevAuthBypassEnabled } from "@/lib/localDevAuthBypass";
import { hasAdminAccess, getLocalhostAccessPreviewMode } from "@/lib/adminAccess";
import { clerkUserCanAccessSg } from "@/lib/regionAccess";
import RequireClientGate from "@/components/auth/RequireClientGate.jsx";
import {
  getRegionFromPath,
  isGlobalPath,
  isUsAppPath,
  legacyRedirectTarget,
  regionHref,
  REGIONS,
  usAppHref,
} from "@/lib/region";

// HomePage is the entry point; other route modules are warmed on navigation intent.
import HomePage from "./pages/HomePage.jsx";
const {
  DashboardPage, YourBusinessPage, DashboardChatsPage, DashboardShellLayout,
  GrantApplicationToolkit, ApplicationGeneratorPage, GrantApplicationSimulatorPage,
  GrantOperationsHubPage, AskOptraPage, GrantComplianceReviewPage,
  GrantComplianceReviewResultsPage, VendorIntelligencePage, VendorProfilePage,
  DashboardFilesPage, IntegrationsPage, OurPartnersPage, AdminAssessmentsPage,
  LoginPage, SubscribePage, AuthCallbackPage, ResultsAsyncEnhanced,
  EligibilityResults, WaitlistPage, PrivacyPage, TermsPage, SingaporeComparePage,
  BlogPostPage, EligibilityAssessmentPage, AdminClientOpsPage, AdminClientDetailPage,
  AdminClientResultsPage, RegionNoAccessPage, RegionChooserPage, GlobalBlogPage,
  GlobalAboutPage, UsSiteRoutes,
} = routePages;

const AccessPending = () => <p className="px-6 py-5 text-sm text-[#667085]" role="status">Checking access...</p>;

// Non-admin on an admin route: back to the dashboard with the denied path in router state so
// the destination can say why (components/auth/NoticeFromState.jsx).
function AdminDeniedRedirect() {
  const location = useLocation();
  return <Navigate to="/dashboard" replace state={{ deniedRoute: location.pathname }} />;
}

function DashboardEntryRoute() {
  return <DashboardPage useExternalShell />;
}

// On localhost, the auth bypass only skips the admin check when no preview
// persona is set; with ?preview=admin/customer the persona must be honored.
function localBypassAllowsAdmin() {
  if (!isLocalDevAuthBypassEnabled()) return false;
  const previewMode = getLocalhostAccessPreviewMode();
  if (!previewMode) return true;
  return hasAdminAccess();
}

/**
 * Region gate for the signed-in Clerk user (see web/src/lib/regionAccess.js). Returns
 * { loaded, allowed }: `allowed` is false only for a well-formed region list without "SG"
 * (a US-only customer); accounts without the flag are Singapore-legacy and pass.
 */
function useSgRegionAccess() {
  const { isLoaded, isSignedIn, user } = useUser();
  if (!isLoaded) return { loaded: false, allowed: true };
  if (!isSignedIn || !user) return { loaded: true, allowed: true };
  return { loaded: true, allowed: clerkUserCanAccessSg(user) };
}

function RequireAdminRouteClerk({ children }) {
  const { isLoaded } = useAuth();
  const region = useSgRegionAccess();
  if (isLocalDevAuthBypassEnabled()) {
    if (localBypassAllowsAdmin()) return children;
    return <AdminDeniedRedirect />;
  }
  if (!isLoaded || !region.loaded) return <AccessPending />;
  // Admins always pass the region gate; anyone else without SG sees the no-access page.
  if (!region.allowed && !hasAdminAccess()) {
    return <RegionNoAccessPage />;
  }
  if (!hasAdminAccess()) {
    return <AdminDeniedRedirect />;
  }
  return children;
}

function RequireAdminRoute({ children }) {
  if (isLocalDevAuthBypassEnabled()) {
    if (localBypassAllowsAdmin()) return children;
    return <AdminDeniedRedirect />;
  }
  if (!isClerkConfigured()) {
    if (!hasAdminAccess()) {
      return <AdminDeniedRedirect />;
    }
    return children;
  }
  return <RequireAdminRouteClerk>{children}</RequireAdminRouteClerk>;
}

// useLocation() is region-relative under <Router basename>. Analytics gets the full path
// ("/sg/dashboard", "/us/pricing") so the regions can be told apart.
function RouteTracker({ region = null }) {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const fullPathname = region ? regionHref(region, location.pathname) : location.pathname;
    const fullRoute = `${fullPathname}${location.search}${location.hash}`;
    window.__optraRoute = fullRoute;
    
    // Track page view for analytics
    if (window.trackPageView) {
      window.trackPageView(fullPathname, document.title);
    }
  }, [region, location.pathname, location.search, location.hash]);

  return null;
}

/**
 * Safety net for plain <a href> elements that React Router does not render (CMS/blog HTML,
 * markdown, legacy markup). index.html has <base href="/">, so without this:
 * - href="#pricing" resolves to "/#pricing", which is now the region chooser, not this page;
 * - href="/blog/x" leaves the region and comes back through the legacy redirect with a full reload.
 * <Link> clicks, handlers that call preventDefault, a bare "/" (the link to the region chooser), new-tab/download links, static files, API paths,
 * links that already carry a region prefix and the US product are all left alone.
 */
function RegionLinkGuard({ routeRootRelativeLinks = false }) {
  const navigate = useNavigate();

  useEffect(() => {
    const onClick = (event) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = typeof event.target?.closest === "function" ? event.target.closest("a[href]") : null;
      if (!anchor || anchor.hasAttribute("download")) return;
      const target = anchor.getAttribute("target");
      if (target && target !== "_self") return;
      const raw = String(anchor.getAttribute("href") || "").trim();

      if (raw.startsWith("#")) {
        event.preventDefault();
        if (raw.length > 1) {
          window.location.hash = raw; // relative to this document, not to <base>
          let id = raw.slice(1);
          try {
            id = decodeURIComponent(id);
          } catch {
            /* keep the raw id */
          }
          document.getElementById(id)?.scrollIntoView({ block: "start" });
        }
        return;
      }

      // A bare "/" is the deliberate link to the region chooser (region switcher): leave it alone.
      if (!routeRootRelativeLinks || raw === "/" || !raw.startsWith("/") || raw.startsWith("//")) return;
      const path = raw.split(/[?#]/)[0];
      if (getRegionFromPath(path) || isUsAppPath(path)) return;
      if (/\.[a-z0-9]{2,5}$/i.test(path)) return;
      if (/^\/(api|v1|export|eligibility|assets|js|media)(\/|$)/i.test(path)) return;
      event.preventDefault();
      navigate(raw);
    };

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [navigate, routeRootRelativeLinks]);

  return null;
}

function VercelSpeedInsights({ region = null }) {
  const { pathname, search } = useLocation();
  const fullPathname = region ? regionHref(region, pathname) : pathname;
  return <SpeedInsights route={`${fullPathname}${search}`} />;
}

const EXTERNAL_GUIDE_URL = "https://docs.optra-labs.com/";

function RedirectToExternalGuide() {
  useEffect(() => {
    window.location.replace(EXTERNAL_GUIDE_URL);
  }, []);
  return null;
}

function RequireAuthLegacy({ children, redirectPath = "/dashboard" }) {
  if (!getSessionEmail()) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirectPath)}`} replace />;
  }
  return children;
}

function RequireAuthClerk({ children, redirectPath = "/dashboard" }) {
  const { isSignedIn, isLoaded } = useAuth();
  const region = useSgRegionAccess();
  if (!isLoaded) return <AccessPending />;
  if (!isSignedIn) return <Navigate to={`/login?redirect=${encodeURIComponent(redirectPath)}`} replace />;

  // Region gate: a US-only account (Clerk public metadata regions without "SG") never reaches
  // the Singapore dashboard. The localhost bypass above is unaffected.
  if (!region.loaded) return <AccessPending />;
  if (!region.allowed) return <RegionNoAccessPage />;

  return children;
}

function RequireAuth({ children, redirectPath = "/dashboard" }) {
  // Local testing escape hatch: only enabled on localhost:5173 in dev mode.
  const localBypass = isLocalDevAuthBypassEnabled();
  if (localBypass) {
    return children;
  }

  const clerkEnabled = isClerkConfigured();
  if (clerkEnabled) {
    return (
      <RequireAuthClerk redirectPath={redirectPath}>{children}</RequireAuthClerk>
    );
  }
  return (
    <RequireAuthLegacy redirectPath={redirectPath}>{children}</RequireAuthLegacy>
  );
}

// Warm page modules on navigation intent; fetching route HTML does not warm lazy imports.
if (typeof window !== 'undefined') {
  const prefetchLink = (e) => {
    // Safety check: ensure target is an Element with closest method
    if (!e.target || typeof e.target.closest !== 'function') {
      return;
    }
    
    try {
      const link = e.target.closest('a[href]');
      if (link && link.href && link.href.startsWith(window.location.origin)) {
        const path = new URL(link.href).pathname;
        // Only prefetch internal routes. link.href is already the full URL (React Router adds the
        // region basename to <Link>), so it compares against window.location.pathname as is.
        // The US product (/us/app) is another server, not a route of this SPA.
        if (path !== window.location.pathname && !path.startsWith('/api') && !isUsAppPath(path)) {
          void preloadRouteModule(path);
        }
      }
    } catch (error) {
      // Silently fail on prefetch errors to avoid breaking navigation
      if (import.meta.env.DEV) {
        console.warn('Prefetch error:', error);
      }
    }
  };
  document.addEventListener('mouseover', prefetchLink, { passive: true });
  document.addEventListener('focusin', prefetchLink, { passive: true });
  document.addEventListener('touchstart', prefetchLink, { passive: true });
}

/** Routes that render inside the dashboard shell: no global header, no reading-progress bar. */
const DASHBOARD_SHELL_ROUTES = [
  "/dashboard",
  "/business",
  "/workflow",
  "/submission-files",
  "/integrations",
  "/our-partners",
  "/grant-roadmap",
  "/application-composer",
  "/grant-application-simulator",
  "/post-approval-operations",
  "/grant-operations-hub",
  "/funding-optimization",
  "/grant-reviewer-simulation",
  "/ask",
  "/grant-compliance-review",
  "/vendor-intelligence",
  "/dashboard/eligibility-assessment",
];

function isDashboardShellPath(pathname) {
  const normalizedPathname = String(pathname || "").toLowerCase();
  return DASHBOARD_SHELL_ROUTES.some((baseRoute) => {
    const normalizedBase = String(baseRoute).toLowerCase();
    return normalizedPathname === normalizedBase || normalizedPathname.startsWith(`${normalizedBase}/`);
  });
}

function AnimatedRoutes() {
  const location = useLocation();
  const deferredLocation = useDeferredValue(location);
  const [transitionStage, setTransitionStage] = useState("fadeIn");
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const rafRef = useRef(null);
  const hideGlobalHeader = isDashboardShellPath(location.pathname);
  
  // Header is now shown on all pages - no feature page exceptions

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setPrefersReducedMotion(media.matches);
    updatePreference();
    media.addEventListener("change", updatePreference);
    return () => media.removeEventListener("change", updatePreference);
  }, []);

  useEffect(() => {
    // Global scroll unlock: drawers/modals/legacy inline locks must not survive navigation.
    forceUnlockDocumentScroll();
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    const hashOnlyChanged =
      location.hash &&
      window.__optraLastPathname === location.pathname &&
      window.__optraLastSearch === location.search;

    if (prefersReducedMotion) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (!hashOnlyChanged) window.scrollTo({ top: 0, behavior: "auto" });
      window.__optraLastPathname = location.pathname;
      window.__optraLastSearch = location.search;
      return;
    }

    // Instant transition - no fade to prevent white flashes
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      if (!hashOnlyChanged) window.scrollTo({ top: 0, behavior: "auto" });
      window.__optraLastPathname = location.pathname;
      window.__optraLastSearch = location.search;
      setTransitionStage("fadeIn");
    });

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [location, prefersReducedMotion]);

  return (
    <>
      {/* Global Header - hidden on dashboard (dashboard has its own persistent sidebar) */}
      {!hideGlobalHeader ? <Header variant="homepage" /> : null}
      <div
        className={`page-transition page-content w-full max-w-full ${transitionStage}`}
        style={{ minHeight: '100vh', paddingTop: hideGlobalHeader ? 0 : 'var(--header-live-h, var(--nav-h, 80px))' }}
      >
        {deferredLocation.key !== location.key ? <RoutePending /> : null}
        <Suspense fallback={<RoutePending />}>
          <Routes location={deferredLocation}>
            <Route path="/" element={<HomePage />} />
            <Route path="/home" element={<HomePage />} />
            {/* Ask OPTRA is a full-viewport workspace with its own conversation sidebar: it sits
                outside the dashboard shell (no dashboard sidebar) but keeps the same auth and
                dashboard_access gate, so a locked customer is still sent back to /dashboard. */}
            <Route
              path="/ask"
              element={
                <RequireAuth redirectPath="/ask">
                  <RequireClientGate gate="dashboard_access">
                    <AskOptraPage />
                  </RequireClientGate>
                </RequireAuth>
              }
            />
            <Route
              element={
                <RequireAuth redirectPath="/dashboard">
                  <DashboardShellLayout />
                </RequireAuth>
              }
            >
              <Route path="/workflow" element={<DashboardPage useExternalShell />} />
              <Route path="/dashboard" element={<DashboardEntryRoute />} />
              <Route
                path="/business"
                element={
                  <RequireClientGate gate="dashboard_access">
                    <YourBusinessPage />
                  </RequireClientGate>
                }
              />
              <Route
                path="/dashboard/chats"
                element={
                  <RequireClientGate gate="dashboard_access">
                    <DashboardChatsPage />
                  </RequireClientGate>
                }
              />
              <Route
                path="/submission-files"
                element={
                  <RequireClientGate gate="dashboard_access">
                    <DashboardFilesPage />
                  </RequireClientGate>
                }
              />
              <Route
                path="/submission-files/:fileId"
                element={
                  <RequireClientGate gate="dashboard_access">
                    <DashboardFilesPage />
                  </RequireClientGate>
                }
              />
              <Route
                path="/integrations"
                element={
                  <RequireAdminRoute>
                    <IntegrationsPage />
                  </RequireAdminRoute>
                }
              />
              <Route path="/our-partners" element={<OurPartnersPage />} />
              <Route path="/dashboard/eligibility-assessment" element={<EligibilityAssessmentPage />} />
              <Route path="/dashboard/assessment-progress" element={<ResultsAsyncEnhanced embedded />} />
              <Route
                path="/dashboard/results"
                element={
                  <RequireClientGate gate="assessment_complete">
                    <ErrorBoundary reloadOnRetry={false}>
                      <EligibilityResults embedded />
                    </ErrorBoundary>
                  </RequireClientGate>
                }
              />
              <Route
                path="/dashboard/admin-assessments"
                element={
                  <RequireAdminRoute>
                    <AdminAssessmentsPage />
                  </RequireAdminRoute>
                }
              />
              <Route
                path="/dashboard/client-ops"
                element={
                  <RequireAdminRoute>
                    <AdminClientOpsPage />
                  </RequireAdminRoute>
                }
              />
              <Route
                path="/dashboard/clients/:clientAccountId"
                element={
                  <RequireAdminRoute>
                    <AdminClientDetailPage />
                  </RequireAdminRoute>
                }
              />
              <Route
                path="/dashboard/clients/:clientAccountId/assessments/:jobId"
                element={
                  <RequireAdminRoute>
                    <ErrorBoundary reloadOnRetry={false}>
                      <AdminClientResultsPage />
                    </ErrorBoundary>
                  </RequireAdminRoute>
                }
              />
              <Route path="/dashboard/files" element={<Navigate to="/submission-files" replace />} />
              <Route path="/grant-application-toolkit" element={<Navigate to="/grant-roadmap" replace />} />
              <Route path="/application-generator" element={<Navigate to="/application-composer" replace />} />
              <Route
                path="/grant-roadmap"
                element={
                  <RequireAdminRoute>
                    <GrantApplicationToolkit />
                  </RequireAdminRoute>
                }
              />
              <Route
                path="/application-composer"
                element={
                  <RequireAdminRoute>
                    <ApplicationGeneratorPage />
                  </RequireAdminRoute>
                }
              />
              <Route
                path="/grant-application-simulator"
                element={
                  <RequireAdminRoute>
                    <GrantApplicationSimulatorPage />
                  </RequireAdminRoute>
                }
              />
              <Route
                path="/post-approval-operations"
                caseSensitive
                element={<GrantOperationsHubPage />}
              />
              <Route
                path="/Post-approval-Operations"
                caseSensitive
                element={<Navigate to="/post-approval-operations" replace />}
              />
              <Route
                path="/grant-operations-hub"
                element={<Navigate to="/post-approval-operations" replace />}
              />
              <Route
                path="/funding-optimization"
                element={<Navigate to="/post-approval-operations" replace />}
              />
              <Route
                path="/grant-reviewer-simulation"
                element={<Navigate to="/post-approval-operations" replace />}
              />
              <Route
                path="/grant-compliance-review"
                element={
                  <RequireAdminRoute>
                    <GrantComplianceReviewPage />
                  </RequireAdminRoute>
                }
              />
              <Route
                path="/grant-compliance-review/results"
                element={
                  <RequireAdminRoute>
                    <GrantComplianceReviewResultsPage />
                  </RequireAdminRoute>
                }
              />
              <Route
                path="/vendor-intelligence"
                element={
                  <RequireAdminRoute>
                    <VendorIntelligencePage />
                  </RequireAdminRoute>
                }
              />
              <Route
                path="/vendor-intelligence/vendor/:id"
                element={
                  <RequireAdminRoute>
                    <VendorProfilePage />
                  </RequireAdminRoute>
                }
              />
            </Route>
            {/* The access-code assessment was retired: every assessment now belongs to a signed-in
                account, so old emailed /access/... links land on the dashboard assessment (sign-in first). */}
            <Route path="/access/*" element={<Navigate to="/dashboard/eligibility-assessment" replace />} />
            <Route path="/shared/eligibility/:token" element={<EligibilityResults />} />
            {/* The /feature/:id placeholder page was retired; old links go home. */}
            <Route path="/feature/:id" element={<Navigate to="/" replace />} />
            <Route path="/login" element={<LoginPage />} />
            {/* Signed-in US-only account on the Singapore product (region gate; see lib/regionAccess.js).
                The page's copy assumes a session ("You're signed in, but…"), so a signed-out visitor
                goes through /login first. */}
            <Route
              path="/no-access"
              element={
                isClerkConfigured() ? (
                  <RequireAuth redirectPath="/no-access">
                    <RegionNoAccessPage />
                  </RequireAuth>
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              }
            />
            <Route path="/subscribe" element={<SubscribePage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/results" element={<Navigate to="/dashboard/assessment-progress" replace />} />
            <Route path="/eligibility-results" element={<Navigate to="/dashboard/results" replace />} />
            <Route path="/get-updates" element={<WaitlistPage />} />
            <Route path="/compare" element={<SingaporeComparePage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            {/* One blog index and one About page for the whole site, at the root (2026-09-23). */}
            <Route path="/blog" element={<HardRedirect to="/blog" />} />
            <Route path="/blog/:id" element={<BlogPostPage />} />
            <Route path="/about" element={<HardRedirect to="/about" />} />
            {/* Unknown URLs redirect to the landing page rather than serving it at the wrong address (soft 404). */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </div>
    </>
  );
}

function MainSiteChrome() {
  const { pathname } = useLocation();
  return (
    <>
      {isDashboardShellPath(pathname) ? null : <ScrollProgress />}
      <div
        className="site-shell optra-marketing flex flex-col"
        style={{
          minHeight: "100vh",
          background: "#FFFFFF",
          flex: 1,
          overflow: "visible",
          alignItems: "stretch",
        }}
      >
        <AnimatedRoutes />
      </div>
    </>
  );
}

/** OPTRA Singapore: the whole pre-region site, mounted under /sg. Internal routes stay region-relative. */
function SingaporeApp() {
  const clerkEnabled = isClerkConfigured();
  const clerkBilling = clerkEnabled && isClerkBillingSyncEnabled();
  const loginOnlyUnlock = clerkEnabled && !clerkBilling;

  return (
    <CountryProvider>
      <Router basename={REGIONS.sg.base}>
          <RouteTracker region="sg" />
          <RegionLinkGuard routeRootRelativeLinks />
          {clerkBilling ? <ClerkEntitlementBridge /> : null}
          {loginOnlyUnlock ? <ClerkLoginUnlockBridge /> : null}
          {clerkEnabled ? <ClientGatesPrefetch /> : null}
          <CalEmbedSetup />

          <Routes>
            <Route path="/guides/*" element={<RedirectToExternalGuide />} />
            <Route path="/docs/*" element={<RedirectToExternalGuide />} />
            <Route path="/guide/*" element={<RedirectToExternalGuide />} />
            <Route path="*" element={<MainSiteChrome />} />
          </Routes>

          <Analytics />
          <VercelSpeedInsights region="sg" />
      </Router>
    </CountryProvider>
  );
}

function HardRedirect({ to }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return <RoutePending />;
}

/** OPTRA US marketing pages under /us. The US product (/us/app) is a different server. */
function UsApp() {
  return (
    <CountryProvider forcedCountry="US">
      <Router basename={REGIONS.us.base}>
        <RouteTracker region="us" />
        <RegionLinkGuard />
        <Suspense fallback={<RoutePending />}>
          <Routes>
            {/* Production answers /us/login with an HTTP redirect (vercel.json); this covers dev. */}
            <Route path="/login" element={<HardRedirect to={usAppHref("/login")} />} />
            <Route path="*" element={<UsSiteRoutes />} />
          </Routes>
        </Suspense>
        <Analytics />
        <VercelSpeedInsights region="us" />
      </Router>
    </CountryProvider>
  );
}

/**
 * /us/app/* belongs to the US Next.js app (Vercel rewrite in production, Vite proxy in dev). The SPA
 * only sees it when that server is not reachable, so say so instead of redirecting in a loop.
 */
function UsAppUnavailable() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-6">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold text-gray-900 mb-2">The OPTRA US app isn&apos;t running</h1>
        <p className="text-gray-600 mb-6">
          This address is served by the OPTRA US application, which did not respond. In local development,
          start the US app on port 3000.
        </p>
        <a className="text-blue-600 underline" href={regionHref("us")}>
          Back to OPTRA US
        </a>
      </div>
    </div>
  );
}

/** No region in the URL: the chooser at "/", and every pre-region URL forwarded to Singapore. */
function GlobalApp({ pathname }) {
  const { search, hash } = window.location;
  const isGuidePath = /^\/(guides|docs|guide)(\/|$)/i.test(pathname);
  const target = legacyRedirectTarget(pathname, search, hash);

  if (isGuidePath) return <RedirectToExternalGuide />;
  if (target) return <HardRedirect to={target} />;
  if (!isGlobalPath(pathname)) return <UsAppUnavailable />;

  return (
    <CountryProvider>
      <Router>
        <RouteTracker />
        {/* "Speak With Us" on the chooser opens the same Cal.com modal as the regional sites. */}
        <CalEmbedSetup />
        <Suspense fallback={<RoutePending />}>
          <Routes>
            <Route path="/blog" element={<GlobalBlogPage />} />
            <Route path="/about" element={<GlobalAboutPage />} />
            <Route path="*" element={<RegionChooserPage />} />
          </Routes>
        </Suspense>
        <Analytics />
        <VercelSpeedInsights />
      </Router>
    </CountryProvider>
  );
}

function App() {
  // Read once: moving between regions (or to the chooser) is always a full page load, never a
  // client-side navigation, because each region is its own router.
  const [pathname] = useState(() => window.location.pathname || "/");
  const region = getRegionFromPath(pathname);

  if (isUsAppPath(pathname)) return <UsAppUnavailable />;
  if (region === "sg") return <SingaporeApp />;
  if (region === "us") return <UsApp />;
  return <GlobalApp pathname={pathname} />;
}

export default App;
// Vercel deployment fix - Thu Aug 28 18:43:30 +08 2025
